import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Affix, Layout, Menu, Tooltip, Typography, Button, Badge } from 'antd';
import styles from './left-menu-sidebar.module.sass';
import cx from 'classnames';
import { FilterProfileSubMenu } from '../FilterProfileSubMenu';
import { EnrichmentSubMenu } from '../EnrichmentSubMenu';
import { ExploreSubMenu } from '../ExploreSubMenu';
import { AttackSurfaceMappingSubMenu } from '../AttackSurfaceMappingSubMenu';
import { ScanDataSubMenu } from '../ScanDataSubMenu';
import { DigitalThreatManagementSubMenu } from '../DigitalThreatManagementSubMenu';
import { MonitoringSubMenu } from '../MonitoringSubMenu';
import { QueryHistorySubMenu } from '../QueryHistorySubMenu';
import { QueryBuilderSubMenu } from '../QueryBuilderSubMenu';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as warning_svg } from '../../../../assets/icons/warning.svg';
import Icon, { DownOutlined, SettingOutlined } from '@ant-design/icons';
import { TitleAction } from '../../shared-components/TitleAction';
import { clearFilterProfile } from '../../../../redux/actions/filters';
import history from '../../../../redux/history';
import { selectOptionsByIndicator } from '../../../../redux/selectors/filters';
import { changeTitle } from '../../../../redux/actions/app';
import * as ROUTES from '../../../../routes';
import { PreferencesSubMenu } from '../PreferencesSubMenu';
import { highlightLeftMenu } from '../../../../redux/actions/leftMenu';
import { BulkDataFeedsSubMenu } from '../BulkDataFeedsSubMenu';
import { DataMarketplaceSubMenu } from '../DataMarketplaceFeedsSubMenu';
import { loadAmberReportCount } from '../../../../redux/actions/blog';
import { STATUS } from '../../../../redux/constants';

const { Sider } = Layout;
const { Title, Text } = Typography;

function LeftMenuSidebar(props) {
  const ThreatIntelligenceIcon = (props) => <Icon component={warning_svg} />;
  const collapsed = useSelector(
    (state) => state.sidebar.leftMenuSideBarCollapsed,
  );
  const advancedFilterMenuOpen = useSelector(
    (state) => state.sidebar.advancedFilterMenuOpen,
  );
  const optionsByIndicator = useSelector((state) =>
    selectOptionsByIndicator(state),
  );
  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const dispatch = useDispatch();
  const location = useLocation();
  const q = useQuery();
  const highlightedMenuItem = useSelector((state) => state.leftMenu.highlight);
  const exploreSchemas = useSelector((state) => state.ioc.exploreSchemas);

  const queryBuilderSubmenuPairs = {
    Domain: 'global-queries-domain',
    Templates: 'template-queries',
    Enrichment: 'global-queries-enrichment',
    Feeds: 'global-queries-feeds',
    IPv4: 'global-queries-ipv4',
    IPv6: 'global-queries-ipv6',
    PADNS: 'global-queries-padns',
    Reputation: 'global-queries-reputation',
    Utils: 'global-queries-utils',
    Xperimental: 'global-queries-xperimental',
  };

  const mainMenuPoints = [
    'threat-intelligence',
    'enrichment',
    'spql',
    'bulk-data-feeds',
    'data-marketplace',
    'query-history',
    'explore',
    'attack-surface-mapping',
    'digital-threat-management',
    'monitoring',
    'query-builder',
    'preferences',
  ];

  //controlling opened submenus
  const [openedMenus, setOpenedMenus] = useState([]);

  //menu item - url pair constants
  const returnUrlMenuPair = (location) => {
    const openMenusInSession = sessionStorage?.getItem('openedMenus');
    let query = location.search?.split('?query=')?.[1]?.split('&')?.[0];
    let selectedSchema;
    let schemaSubmenuName;

    // queries
    if (
      location.search?.includes('?query=') ||
      location.pathname?.includes('?query=')
    ) {
      if (exploreSchemas?.form_list?.length > 0) {
        if (query === undefined) {
          query = location.pathname?.split('?query=')?.[1]?.split('&')?.[0];
        }

        selectedSchema = exploreSchemas?.form_list?.filter(
          (o) => o?.form_uuid === query,
        )?.[0];
        const schemaName = selectedSchema?.form_name?.split(' ')?.[0];
        schemaSubmenuName = Object.entries(queryBuilderSubmenuPairs)?.find(
          ([pairKey]) => pairKey === schemaName,
        );
        schemaSubmenuName = schemaSubmenuName
          ? schemaSubmenuName?.[1]
          : undefined;
      }
    }

    // recipes
    if (
      location.pathname?.includes('/explore/') &&
      !location.pathname?.includes('query-builder')
    ) {
      let recipe = null;

      if (location?.search) {
        if (location.search?.includes('?left_menu_name=')) {
          recipe = location?.pathname
            ?.split('/explore/')?.[1]
            ?.split('?')?.[0]
            ?.replace('/', '');
          return [recipe, recipe + '-submenu'];
        }
        recipe = location?.search?.split('?recipeid=')?.[1]?.split('&')?.[0];
      } else {
        recipe = location?.pathname?.split('/explore/')?.[1];
        return openMenusInSession?.includes('explore')
          ? [recipe, 'explore', recipe]
          : [recipe, recipe];
      }

      return [recipe + '-savedRecipe', recipe + '-savedRecipe'];
    }

    // filter profiles
    if (location.search?.includes('?filterProfile=')) {
      const filter = location.search
        ?.split('?filterProfile=')?.[1]
        ?.replace('?openAdvancedFilter=true', '');
      return openMenusInSession?.includes('filter-profiles')
        ? [filter, 'filter-profiles']
        : [filter, 'threat-intelligence'];
    }

    switch (location.pathname?.split('/?query=')?.[0]) {
      case '/':
        return [
          advancedFilterMenuOpen
            ? 'create-new-filter-profile-menu-item'
            : process.env.REACT_APP_FRONTEND_NODE_ENV === 'explore' ||
              process.env.REACT_APP_FRONTEND_NODE_ENV === 'dev-explore'
            ? 'advanced-search-menu-item'
            : 'threat-ranking-menu-item',
          process.env.REACT_APP_FRONTEND_NODE_ENV === 'explore' ||
          process.env.REACT_APP_FRONTEND_NODE_ENV === 'dev-explore'
            ? 'explore'
            : 'threat-intelligence',
        ];
      case '/explore':
        return ['advanced-search-menu-item', 'explore'];
      case '/bulk-data-feeds':
        return ['bulk-data-feeds-menu-item', 'bulk-data-feeds'];
      case '/data-marketplace':
        return ['data-marketplace-menu-item', 'data-marketplace'];
      case '/web-scanner':
        return ['spql-menu-item', 'spql'];
      case '/enrichment':
        return ['enrichment-menu-item', 'enrichment'];
      case '/explore-result':
        return ['advanced-search-menu-item', 'explore'];
      case '/indicators':
        return ['organization-assets-menu-item', 'preferences'];
      case '/explore/query-builder':
        return [query, 'query-builder', schemaSubmenuName + '-submenu'];
      case '/filter-profiles':
        return ['filter-profiles-menu-item', 'preferences'];
      case '/monitoring':
        return ['monitoring-menu-item', 'monitoring'];
      case '/blog':
        return ['blog-menu-item', 'blog'];
      case '/profile':
        return null;
      case '/account':
        return null;
      case '/organization':
        return null;
      case '/subscription':
        return null;
      case '/customers':
        return null;
      case '/customers/impersonate':
        return null;
      default:
        return false;
    }
  };

  const limitOpenedMenusOnMobile = (openMenus) => {
    if (collapsed) {
      setOpenedMenus(openMenus);
      sessionStorage.setItem('openedMenus', openMenus);
    }
  };

  useEffect(() => {
    const opened = sessionStorage.getItem('openedMenus');
    if (opened) {
      setOpenedMenus(opened?.split(','));
    }
    dispatch(highlightLeftMenu(sessionStorage.getItem('selectedMenuItem')));
  }, []);

  useEffect(() => {
    if (
      props?.mySubscriptionPlan?.has_access_to_tlp_amber_report &&
      props?.mySubscriptionPlanStatus === STATUS.LOADED
    ) {
      dispatch(loadAmberReportCount());
    }
  }, [props?.mySubscriptionPlanStatus]);

  useEffect(() => {
    if (collapsed) {
      setOpenedMenus([]);
      sessionStorage.setItem('openedMenus', []);
    }

    const menuItemPair = returnUrlMenuPair(location);

    if (menuItemPair !== false) {
      if (menuItemPair !== null) {
        dispatch(highlightLeftMenu(menuItemPair?.[0]));
        if (!openedMenus?.includes(menuItemPair?.[1])) {
          toggleKey(menuItemPair?.[1]);
        }
        if (menuItemPair?.[2] !== undefined) {
          if (!openedMenus?.includes(menuItemPair?.[2])) {
            toggleKey(menuItemPair?.[2]);
          }
        }
      } else {
        dispatch(highlightLeftMenu(menuItemPair));
      }
    }
  }, [location]);

  useEffect(() => {
    if (collapsed) {
      setOpenedMenus([]);
      sessionStorage.setItem('openedMenus', []);
    }
  }, [collapsed]);

  const toggleKey = (collapseKey) => {
    if (!props.leftMenuSideBarCollapsed) {
      const mainMenuTriggered = mainMenuPoints?.includes(collapseKey);

      if (openedMenus.indexOf(collapseKey) !== -1) {
        const opened = openedMenus?.filter((o) => o !== collapseKey);
        mainMenuTriggered ? setOpenedMenus([]) : setOpenedMenus(opened);
        sessionStorage.removeItem('openedMenus');
        mainMenuTriggered
          ? sessionStorage.setItem('openedMenus', [])
          : sessionStorage.setItem('openedMenus', opened);
      } else {
        let opened = sessionStorage?.getItem('openedMenus')?.split(',') ?? [];

        if (
          collapseKey === 'explore' &&
          q?.get('backLink') &&
          !location.pathname === '/enrichment'
        ) {
          //store secondary storage in case of navigating backwards from explore in the future
          sessionStorage.setItem('openedMenusBefore', opened);
        }

        if (
          collapseKey?.includes('-submenu') ||
          collapseKey?.includes('-savedRecipe')
        ) {
          //closing other submenus if they are open
          if (collapseKey?.includes('-submenu')) {
            opened = opened.filter((o) => !o.includes('-submenu'));
          }

          //on backwards navigation get previously opened menus and use them as opened ones
          const openedBefore = sessionStorage
            .getItem('openedMenusBefore')
            ?.split(',');
          if (openedBefore?.length > 0 && !q?.get('backLink')) {
            opened = openedBefore;
            sessionStorage.removeItem('openedMenusBefore');
          }
        }

        if (
          !opened?.includes(collapseKey) &&
          !collapseKey.includes('-savedRecipe')
        ) {
          opened.push(collapseKey);
        }

        mainMenuTriggered
          ? setOpenedMenus([collapseKey])
          : setOpenedMenus(opened);
        sessionStorage.removeItem('openedMenus');
        mainMenuTriggered
          ? sessionStorage.setItem('openedMenus', [collapseKey])
          : sessionStorage.setItem('openedMenus', opened);
      }
    }
  };

  const setSessionStorage = (menuItem) => {
    dispatch(highlightLeftMenu(menuItem));

    sessionStorage.removeItem('selectedMenuItem');
    sessionStorage.setItem('selectedMenuItem', [menuItem]);
  };

  const isKeyOn = (menuKey) => {
    return openedMenus.indexOf(menuKey) !== -1;
  };

  const returnSubstituteMenu = (data) => {
    if (data.type === 'menuItem') {
      return (
        <Tooltip title={<Text>{data.tooltipMessage}</Text>} placement={'right'}>
          <div className={styles.substitute_menu_option}>
            <Text strong>{data.menuTitle}</Text>
            <DownOutlined />
          </div>
        </Tooltip>
      );
    }

    if (data.type === 'subMenuItem') {
      return (
        <Tooltip title={<Text>{data.tooltipMessage}</Text>} placement={'right'}>
          <div
            className={cx(styles.substitute_menu_option, styles.subMenuItem)}
          >
            <Text>{data.menuTitle}</Text>
          </div>
        </Tooltip>
      );
    }

    if (data.type === 'innerSubmenuItem') {
      return (
        <Tooltip title={<Text>{data.tooltipMessage}</Text>} placement={'right'}>
          <div
            className={cx(
              styles.substitute_menu_option,
              styles.innerSubmenuItem,
            )}
          >
            <Text>{data.menuTitle}</Text>
          </div>
        </Tooltip>
      );
    }

    if (data.type === 'subMenuButton') {
      return (
        <Tooltip title={<Text>{data.tooltipMessage}</Text>} placement={'right'}>
          <Button className={styles.add_new_button} type="link">
            {data.menuTitle}
          </Button>
        </Tooltip>
      );
    }

    if (data.type === 'subMenu') {
      return (
        <Tooltip title={<Text>{data.tooltipMessage}</Text>} placement={'right'}>
          <Menu.SubMenu
            title={
              <TitleAction
                title={
                  <div className={styles.section_title}>
                    {data.icon}
                    <Title level={5}>{data.menuTitle}</Title>
                  </div>
                }
              />
            }
            className={cx(styles.substitute_mainmenu_option, styles.disabled)}
          ></Menu.SubMenu>
        </Tooltip>
      );
    }
  };

  return (
    <Affix
      offsetTop={70}
      className={cx(styles.left_menu_sidebar, {
        [styles.collapsed]: collapsed,
      })}
    >
      <Sider trigger={null} collapsible collapsed={collapsed} width={260}>
        <Menu
          openKeys={openedMenus}
          selectedKeys={[highlightedMenuItem]}
          inlineIndent={10}
          mode="inline"
          theme="dark"
          expandIcon={<DownOutlined data-main-indicator />}
          onOpenChange={limitOpenedMenusOnMobile}
        >
          {/* <BulkDataFeedsSubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            key={'bulk-data-feeds'}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('bulk-data-feeds');
            }}
          /> */}

          <DataMarketplaceSubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            key={'data-marketplace'}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('data-marketplace');
            }}
          />

          <Menu.SubMenu
            data-cy={`leftMenuSidebarThreatIntelligenceManagement`}
            className={styles.main_menu_option}
            onTitleClick={() => {
              toggleKey('threat-intelligence');
            }}
            key={'threat-intelligence'}
            title={
              <TitleAction
                menuKey={'threat-intelligence'}
                title={
                  <div className={styles.section_title}>
                    <div className={styles.border} />
                    <ThreatIntelligenceIcon />
                    <Title level={5}>Threat Intelligence Management</Title>
                  </div>
                }
                showDownArrow={isKeyOn('threat-intelligence')}
              />
            }
          >
            {props?.mySubscriptionPlan?.has_access_to_tlp_amber_report ? (
              <Menu.Item
                style={{ paddingRight: '3rem' }} //this is for the unread count badge
                data-cy="leftMenuSidebarTlpAmberReports"
                onClick={() => {
                  dispatch(changeTitle('TLP Amber Reports', null));
                  setSessionStorage('blog-menu-item');
                  history.push('/blog');
                }}
                key={'blog-menu-item'}
                disabled={
                  !props?.mySubscriptionPlan?.has_access_to_tlp_amber_report
                }
              >
                <Link to="/blog">
                  {props?.amberReportCount.count === 0 ||
                  !props?.mySubscriptionPlan?.has_access_to_tlp_amber_report ? (
                    <Text className={styles.additional_indentation} strong>
                      TLP Amber Reports
                    </Text>
                  ) : (
                    <Tooltip title="Number of unread reports" placement="right">
                      <div>
                        <Badge
                          count={props?.amberReportCount.count}
                          size="small"
                          offset={[20, 0]}
                        >
                          <Text
                            className={styles.additional_indentation}
                            strong
                          >
                            TLP Amber Reports
                          </Text>
                        </Badge>
                      </div>
                    </Tooltip>
                  )}
                </Link>
              </Menu.Item>
            ) : (
              returnSubstituteMenu({
                type: 'menuItem',
                tooltipMessage: (
                  <>
                    TLP Amber Reports is not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                ),
                menuTitle: 'TLP Amber Reports',
                icon: null,
              })
            )}

            {props?.mySubscriptionPlan?.has_access_to_sources ? (
              <Menu.Item
                data-cy="leftMenuSidebarFeeds"
                onClick={() => {
                  dispatch(changeTitle('Feeds', null));
                  setSessionStorage('feeds-menu-item');
                  history.push('/sources/feed?scope=all');
                }}
                key={'feeds-menu-item'}
                disabled={!props?.mySubscriptionPlan?.has_access_to_sources}
              >
                <Link to="/sources/feed?scope=all">
                  <Text className={styles.additional_indentation} strong>
                    Feeds
                  </Text>
                </Link>
              </Menu.Item>
            ) : (
              returnSubstituteMenu({
                type: 'menuItem',
                tooltipMessage: (
                  <>
                    Feeds are not part of this subscription plan. To upgrade
                    your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                ),
                menuTitle: 'Feeds',
                icon: null,
              })
            )}

            {props?.mySubscriptionPlan?.has_access_to_sources ? (
              <Menu.Item
                data-cy="leftMenuSidebarCollections"
                onClick={() => {
                  dispatch(changeTitle('Collections', null));
                  setSessionStorage('collections-menu-item');
                  history.push('/sources/collection?scope=all');
                }}
                key={'collections-menu-item'}
                disabled={!props?.mySubscriptionPlan?.has_access_to_sources}
              >
                <Link to="/sources/collection?scope=all">
                  <Text className={styles.additional_indentation} strong>
                    Collections
                  </Text>
                </Link>
              </Menu.Item>
            ) : (
              returnSubstituteMenu({
                type: 'menuItem',
                tooltipMessage: (
                  <>
                    Collections are not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                ),
                menuTitle: 'Collections',
                icon: null,
              })
            )}

            {props?.mySubscriptionPlan?.has_access_to_sources ? (
              <Menu.Item
                data-cy="leftMenuSidebarFeedReports"
                onClick={() => {
                  dispatch(changeTitle('Feeds Comparison', null));
                  setSessionStorage('feeds-report-menu-item');
                  history.push(ROUTES.FEED_REPORTS);
                }}
                key={'feeds-report-menu-item'}
                disabled={!props?.mySubscriptionPlan?.has_access_to_sources}
              >
                <Link to="/feeds_report">
                  <Text className={styles.additional_indentation} strong>
                    Feeds Report
                  </Text>
                </Link>
              </Menu.Item>
            ) : (
              returnSubstituteMenu({
                type: 'menuItem',
                tooltipMessage: (
                  <>
                    Feed Reports are not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                ),
                menuTitle: 'Feeds Report',
                icon: null,
              })
            )}

            {props?.mySubscriptionPlan?.has_access_to_threat_ranking ? (
              <Menu.Item
                data-cy="leftMenuSidebarThreatRanking"
                onClick={() => {
                  dispatch(changeTitle('Threat Ranking'));
                  props.updateTag(null);
                  history.push('/');
                  setSessionStorage('threat-ranking-menu-item');
                  dispatch(clearFilterProfile());
                }}
                key="threat-ranking-menu-item"
                disabled={
                  !props?.mySubscriptionPlan?.has_access_to_threat_ranking
                }
              >
                <Link to={`/`}>
                  <Text className={styles.additional_indentation} strong>
                    Threat Ranking
                  </Text>
                </Link>
              </Menu.Item>
            ) : (
              returnSubstituteMenu({
                type: 'menuItem',
                tooltipMessage: (
                  <>
                    Threat Ranking is not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                ),
                menuTitle: 'Threat Ranking',
                icon: null,
              })
            )}

            <FilterProfileSubMenu
              returnSubstituteMenu={returnSubstituteMenu}
              mySubscriptionPlan={props?.mySubscriptionPlan}
              mySubscriptionPlanStatus={props?.mySubscriptionPlanStatus}
              key={'filter-profiles'}
              isKeyOn={isKeyOn}
              toggleKey={toggleKey}
              setSessionStorage={setSessionStorage}
              optionsByIndicator={optionsByIndicator}
              advancedFilterMenuOpen={advancedFilterMenuOpen}
              openEditAdvancedFilter={props?.openEditAdvancedFilter}
              openCreateNewAdvancedFilter={props?.openCreateNewAdvancedFilter}
            />
          </Menu.SubMenu>
          <EnrichmentSubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            key={'enrichment'}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('enrichment');
            }}
          />

          <ScanDataSubMenu
            mySubscriptionPlan={props?.mySubscriptionPlan}
            returnSubstituteMenu={returnSubstituteMenu}
            key={'spql'}
            toggleKey={toggleKey}
            onTitleClick={() => {
              toggleKey('spql');
            }}
            isKeyOn={isKeyOn}
            setSessionStorage={setSessionStorage}
          />
          <ExploreSubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            key={'explore'}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('explore');
            }}
          />
          <AttackSurfaceMappingSubMenu
            setClearRecipe={props.setClearRecipe}
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('attack-surface-mapping');
            }}
            key={'attack-surface-mapping'}
          />
          <DigitalThreatManagementSubMenu
            setClearRecipe={props.setClearRecipe}
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('digital-threat-management');
            }}
            key={'digital-threat-management'}
          />
          <QueryHistorySubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('query-history');
            }}
            key={'query-history'}
          />
          <MonitoringSubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('monitoring');
            }}
            key={'monitoring'}
          />
          <QueryBuilderSubMenu
            returnSubstituteMenu={returnSubstituteMenu}
            mySubscriptionPlan={props?.mySubscriptionPlan}
            isKeyOn={isKeyOn}
            toggleKey={toggleKey}
            setSessionStorage={setSessionStorage}
            onTitleClick={() => {
              toggleKey('query-builder');
            }}
            key={'query-builder'}
          />
          {props?.mySubscriptionPlan?.has_access_to_preferences ? (
            <PreferencesSubMenu
              mySubscriptionPlan={props?.mySubscriptionPlan}
              isKeyOn={isKeyOn}
              toggleKey={toggleKey}
              setSessionStorage={setSessionStorage}
              onTitleClick={() => {
                toggleKey('preferences');
              }}
              key={'preferences'}
            />
          ) : (
            returnSubstituteMenu({
              type: 'subMenu',
              tooltipMessage: (
                <>
                  Preferences options are not part of this subscription plan. To
                  upgrade your subscription contact us at{' '}
                  <a target="_blank" href="mailto:sales@silentpush.com">
                    sales@silentpush.com
                  </a>
                  .
                </>
              ),
              menuTitle: 'Preferences',
              icon: <SettingOutlined />,
            })
          )}
        </Menu>
      </Sider>
    </Affix>
  );
}

export default LeftMenuSidebar;
